import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listAllFacilities } from '../../api/catalogues.js'
import { useDebounce } from '../../hooks/useDebounce.js'

// Combined search + pick for the HealthFacility directory.
//
// Design notes:
//  - On first focus the dropdown shows the full active directory (optionally
//    narrowed by facility_type / specialty). There's no "type 2+ characters"
//    gate — the total directory is small (currently ~27 rows) so eager
//    loading is fine and lets clinicians *browse* the list when they haven't
//    decided which facility to refer to.
//  - Typing debounces 200ms and re-queries with ?search=, narrowing live.
//  - Once a facility is picked we swap the input for a "selected" pill card
//    so the user sees what they've chosen and can clear it with ×.
//
// Props:
//   facilityType   (string)   - filters by facility_type on the server
//   specialty      (string)   - filters by ?specialty=<icontains>
//   value          (id|null)  - currently selected facility id
//   selected       (object)   - full selected facility row (for display)
//   onPick({...})             - called with the full facility row on pick
//   onClear()                 - called when user clicks × on the pill
const FACILITY_TYPE_LABEL = {
  hospital: 'Hospital',
  specialist: 'Specialist',
  clinic: 'Clinic',
  health_centre: 'Health Centre',
  aid_post: 'Aid Post',
}

export default function FacilityAutocomplete({
  facilityType = '',
  specialty = '',
  value,
  selected,
  onPick,
  onClear,
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)
  const debounced = useDebounce(q, 200)

  const { data = [], isFetching } = useQuery({
    queryKey: ['facilities-autocomplete', facilityType, specialty, debounced],
    queryFn: () =>
      listAllFacilities({
        facility_type: facilityType || undefined,
        specialty: specialty || undefined,
        search: debounced || undefined,
      }),
    staleTime: 5 * 60_000,
    // Always fetch — empty q still lists the directory.
    enabled: !value,
  })

  // Reset highlight when the data set changes so "Enter" never picks a stale row
  useEffect(() => {
    setHighlight(0)
  }, [debounced, facilityType, specialty])

  function pick(item) {
    onPick?.(item)
    setQ('')
    setOpen(false)
    setHighlight(0)
  }

  function onKeyDown(e) {
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((i) => Math.min(i + 1, data.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (data[highlight]) pick(data[highlight])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // ---- Selected pill view -------------------------------------------------
  if (value && selected) {
    return (
      <div className="border border-wantok-200 bg-wantok-50 rounded-md p-3 text-sm">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-1.5 py-0.5 bg-white text-wantok-700 border border-wantok-200 rounded text-[10px] uppercase">
                {FACILITY_TYPE_LABEL[selected.facility_type] || selected.facility_type}
              </span>
              <span className="font-semibold text-slate-800">{selected.name}</span>
              {selected.specialty && (
                <span className="text-slate-600">· {selected.specialty}</span>
              )}
            </div>
            {(selected.town || selected.province) && (
              <div className="text-xs text-slate-600 mt-0.5">
                {[selected.town, selected.province].filter(Boolean).join(', ')}
              </div>
            )}
            {selected.address && (
              <div className="text-xs text-slate-500">{selected.address}</div>
            )}
            {selected.phone_number && (
              <div className="text-xs text-slate-500">☎ {selected.phone_number}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-slate-400 hover:text-rose-600 text-lg leading-none"
            aria-label="Change facility"
          >
            ×
          </button>
        </div>
      </div>
    )
  }

  // ---- Search + list view -------------------------------------------------
  const showMenu = open || (!!q && data.length > 0)
  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Search facilities by name, town, or specialty…"
        className="w-full border rounded px-3 py-2 text-sm"
        inputMode="search"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={showMenu}
      />
      {showMenu && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto text-sm">
          {isFetching && data.length === 0 && (
            <div className="p-2 text-slate-400">Searching…</div>
          )}
          {!isFetching && data.length === 0 && (
            <div className="p-2 text-slate-400">
              No facilities match. Try clearing the filter or searching differently.
            </div>
          )}
          {data.map((item, i) => (
            <button
              type="button"
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault()
                pick(item)
              }}
              onMouseEnter={() => setHighlight(i)}
              className={`block w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 ${
                i === highlight ? 'bg-wantok-50' : 'hover:bg-slate-50'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-1.5 py-0.5 bg-wantok-50 text-wantok-700 border border-wantok-100 rounded text-[10px] uppercase">
                  {FACILITY_TYPE_LABEL[item.facility_type] || item.facility_type}
                </span>
                <span className="font-medium text-slate-800">{item.name}</span>
                {item.specialty && (
                  <span className="text-slate-500 text-xs">· {item.specialty}</span>
                )}
              </div>
              {(item.town || item.province) && (
                <div className="text-xs text-slate-500 mt-0.5">
                  {[item.town, item.province].filter(Boolean).join(', ')}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
